import { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, apiError, logError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const POLL_MS = 30000;

/** Pops delegated work onto the employee's screen (laptop or phone) and pings the device. */
export function TaskPopup() {
  const { user } = useAuth();
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState("");
  const notified = useRef(new Set());

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/tasks/popup");
      setQueue(data);
      if (data.length && "Notification" in window && Notification.permission === "granted") {
        data.forEach((t) => {
          if (notified.current.has(t.id)) return;
          notified.current.add(t.id);
          new Notification(`New work: ${t.title}`, { body: t.message, tag: t.id });
        });
      }
    } catch (e) {
      logError("task popup", e);
    }
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    poll();
    const t = setInterval(poll, POLL_MS);
    return () => clearInterval(t);
  }, [user?.id, poll]);

  useEffect(() => {
    if (!user?.id) return;
    if ("Notification" in window && Notification.permission === "default")
      Notification.requestPermission().catch(() => {});
  }, [user?.id]);

  const task = queue[0];
  if (!task) return null;

  const act = async (path) => {
    setBusy(true);
    try {
      await api.post(`/tasks/${task.id}/${path}`, { reply });
      setQueue((q) => q.slice(1));
      setReply("");
      toast.success(path === "done" ? "Marked done" : "Accepted");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const later = async () => {
    try {
      await api.post(`/tasks/${task.id}/seen`);
    } catch (e) {
      logError("task seen", e);
    }
    setQueue((q) => q.slice(1));
  };

  return (
    <Dialog open onOpenChange={(o) => !o && later()}>
      <DialogContent className="sm:max-w-md" data-testid="task-popup">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            {task.title}
            {task.priority !== "Normal" && (
              <Badge className="text-[10px]">{task.priority}</Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            From {task.assigned_by_name}
            {task.due_date ? ` • due ${task.due_date}` : ""}
          </DialogDescription>
        </DialogHeader>

        <p className="rounded-lg border-l-4 border-primary bg-secondary p-3 text-sm"
          data-testid="task-popup-message">
          {task.message}
        </p>

        <Input
          placeholder="Reply to the owner (optional)"
          data-testid="task-popup-reply"
          value={reply}
          onChange={(e) => setReply(e.target.value)}
        />

        <DialogFooter className="gap-2">
          <Button variant="ghost" data-testid="task-popup-later" onClick={later}>Later</Button>
          <Button variant="outline" data-testid="task-popup-accept" disabled={busy}
            onClick={() => act("accept")}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Accept
          </Button>
          <Button data-testid="task-popup-done" className="gap-1.5" disabled={busy}
            onClick={() => act("done")}>
            <CheckCircle2 className="h-4 w-4" /> Mark done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
